// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { classNames } from '@shared/ui-kit/utils';
import {
  type ButtonHTMLAttributes,
  type FocusEventHandler,
  forwardRef,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { makeContentId, makeTriggerId, useTabsContext } from './tabs-context';
import styles from './tabs.module.css';
import { AppIcon } from '../app-icon';
import { Tooltip } from '@mui/material';

export interface TabsTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly value: string;
  readonly error?: boolean;
  readonly errorMessage?: string | ReactNode;
}

export const useTabsTrigger = (props: TabsTriggerProps) => {
  const context = useTabsContext();

  const triggerId = makeTriggerId(context.baseId, props.value);

  const contentId = makeContentId(context.baseId, props.value);

  const isSelected = useMemo(
    () => props.value === context.value,
    [props.value, context.value],
  );

  const onTabFocus: FocusEventHandler<HTMLButtonElement> = useCallback(
    event => {
      if (!isSelected && !props.disabled) {
        context.onValueChange(props.value);
      }

      props.onFocus?.(event);
    },
    [isSelected, props, context],
  );

  return {
    onTabFocus,
    isSelected,
    triggerId,
    contentId,
  };
};

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ children, disabled, className, error, errorMessage, ...props }, ref) => {
    const { contentId, isSelected, onTabFocus, triggerId } = useTabsTrigger({
      disabled,
      ...props,
    });
    const { variant, size } = useTabsContext();

    const triggerClassName = useMemo(() => {
      if (variant === 'underline') {
        return styles['tabs-trigger--underline'];
      }

      if (variant === 'segmented') {
        return classNames(
          styles['tabs-trigger--segmented'],
          size === 'sm'
            ? styles['tabs-trigger--segmented-sm']
            : styles['tabs-trigger--segmented-md'],
        );
      }

      return classNames(
        styles['tabs-trigger'],
        size === 'sm' && styles['tabs-trigger--sm'],
      );
    }, [size, variant]);

    return (
      <button
        type="button"
        role="tab"
        aria-selected={isSelected}
        aria-controls={contentId}
        data-state={isSelected ? 'active' : 'inactive'}
        aria-disabled={disabled}
        disabled={disabled}
        id={triggerId}
        onFocus={onTabFocus}
        className={classNames(triggerClassName, className)}
        data-testid={triggerId}
        {...props}
        ref={ref}
      >
        <span className="flex items-center justify-center gap-3">
          {children}
          {error && (
            <Tooltip title={errorMessage} placement="top">
              <AppIcon
                icon="Warning"
                size="lg"
                className="self-center !text-warning"
                data-testid={`${triggerId}-error-icon`}
              />
            </Tooltip>
          )}
        </span>
      </button>
    );
  },
);

TabsTrigger.displayName = 'TabsTrigger';
